import {MiitelSetting} from 'api';

// MiiTel公式ドキュメント
// https://support.miitel.jp/hc/ja/articles/360030159052-JavaScript-%E3%82%A6%E3%82%A3%E3%82%B8%E3%82%A7%E3%83%83%E3%83%88
//
// v1.js は読み込むたびに window に message リスナーを追加し、ページを再読み込みするまで消えない。
// 画面ごとに読み込み直すと、1回の発信で onReceiveSequenceId がリスナーの数だけ呼ばれてしまう(#17211)。
// そのため、ウィジェットはページ全体で1回だけ読み込み、画面を離れるときは消さずに非表示にする。
// (消すと通話中の iframe も消えて電話が切れる)

type CreateActivity = (miitelSequenceId: string) => Promise<unknown>;

const SCRIPT_ID = 'miitel-widget-script';
const HIDDEN_CLASS = 'miitel-widget-hidden';

// いま開いている画面の記録先
let currentTarget: CreateActivity | null = null;
// 発信ボタンを押した画面の記録先。発信後に別のリードを開いても、かけたリードに記録する
let dialTarget: CreateActivity | null = null;
let mountedCount = 0;
// 発信してから通話が終わるまで true。ページを読み込み直してよいかの判断に使う(#17214)
let inCall = false;
const sentSequenceIds = new Set<string>();

const onReceiveSequenceId = (e: {eventName: string; sequenceId: string}) => {
  if (sentSequenceIds.has(e.sequenceId)) return;

  // 着信など、この画面から発信していない通話は開いている画面に記録する
  const target = dialTarget || currentTarget;
  dialTarget = null;
  if (!target) return;

  sentSequenceIds.add(e.sequenceId);
  // 送信に失敗したら忘れて、次のイベントで再送できるようにする
  target(e.sequenceId).catch(() => sentSequenceIds.delete(e.sequenceId));
};

const loadWidget = (miitelSetting: MiitelSetting) => {
  if (document.getElementById(SCRIPT_ID)) return;

  const style = document.createElement('style');
  style.textContent = `
    body.${HIDDEN_CLASS} #miitelPhoneIFrameButton,
    body.${HIDDEN_CLASS} #miitelPhoneIFrameOuter {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  const w = window as any;
  w.miitelWidget =
    w.miitelWidget ||
    function (k: string, v: unknown) {
      w.miitelWidget.conf = w.miitelWidget.conf || {};
      w.miitelWidget.conf[k] = v;
    };
  w.miitelWidget('company_id', miitelSetting.miitelCompanyId);
  w.miitelWidget('access_key', miitelSetting.accessKey);
  w.miitelWidget('onReceiveSequenceId', onReceiveSequenceId);
  w.miitelWidget('onReceiveCall', () => (inCall = true));
  w.miitelWidget('onDialBegin', () => (inCall = true));
  w.miitelWidget('onCallBegin', () => (inCall = true));
  w.miitelWidget('onCallEnd', () => (inCall = false));

  const scriptTag = document.createElement('script');
  scriptTag.type = 'text/javascript';
  scriptTag.src = 'https://api.miitel.jp/static/widget/v1.js';
  scriptTag.id = SCRIPT_ID;
  document.body.appendChild(scriptTag);
};

// ウィジェットを表示し、この画面での通話の記録先を登録する。戻り値は後片付け用の関数
// 記録しない画面では createActivity に null を渡す
export const mountMiitelWidget = (
  miitelSetting: MiitelSetting,
  createActivity: CreateActivity | null,
) => {
  loadWidget(miitelSetting);

  mountedCount += 1;
  currentTarget = createActivity;
  document.body.classList.remove(HIDDEN_CLASS);

  return () => {
    mountedCount -= 1;
    if (currentTarget === createActivity) currentTarget = null;
    if (mountedCount === 0) document.body.classList.add(HIDDEN_CLASS);
  };
};

export const miitelCall = (telephoneNumber: string) => {
  if (confirm(`「${telephoneNumber}」に発信してよろしいですか？`)) {
    dialTarget = currentTarget;
    inCall = true;
    (window as any).miitelWidget.call(telephoneNumber);
  }
};

export const isMiitelInCall = () => inCall;
